import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';

const prefersReducedMotion = (): boolean =>
    window.matchMedia(reducedMotionQuery).matches;

const hasInitialHash = (): boolean =>
    window.location.hash.length > 1;

const setText = (selector: string, value: string | undefined, root: ParentNode = document): void => {
    const element = root.querySelector<HTMLElement>(selector);
    if (element && value !== undefined && element.textContent !== value) {
        element.textContent = value;
    }
};

const setLandingScene = (page: HTMLElement, section: HTMLElement): void => {
    const scene = section.dataset.landingScene ?? 'hero';
    page.dataset.currentScene = scene;
    setText('[data-landing-scene-kicker]', section.dataset.sceneKicker, page);
};

const isElementInViewport = (element: HTMLElement, topRatio = 0.9): boolean => {
    const rect = element.getBoundingClientRect();

    return rect.top < window.innerHeight * topRatio && rect.bottom > window.innerHeight * 0.08;
};

const activateStoryStep = (
    page: HTMLElement,
    story: HTMLElement,
    steps: HTMLElement[],
    step: HTMLElement
): void => {
    const tone = step.dataset.storyTone ?? 'vermilion';
    const mode = step.dataset.storyMode ?? 'system';

    story.dataset.storyTone = tone;
    steps.forEach(candidate => candidate.classList.toggle('is-active', candidate === step));

    const map = story.querySelector<HTMLElement>('[data-landing-system-map]');
    if (map) {
        map.dataset.mode = mode;
    }

    setText('[data-landing-stage-symbol]', step.dataset.storySymbol, story);
    setText('[data-landing-stage-kicker]', step.dataset.storyKicker, story);

    page.dispatchEvent(new CustomEvent('landing-story-active', { detail: { id: step.id } }));
};

const initLandingReveals = (page: HTMLElement): void => {
    const targets = Array.from(page.querySelectorAll<HTMLElement>('[data-landing-reveal]'));
    if (!targets.length) {
        return;
    }

    if (prefersReducedMotion() || hasInitialHash() || !('IntersectionObserver' in window)) {
        targets.forEach(target => target.classList.add('is-visible'));
        return;
    }

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) {
                return;
            }

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
        });
    }, {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.18
    });

    targets.forEach(target => observer.observe(target));

    const revealVisibleTargets = (): void => {
        targets.forEach(target => {
            if (!target.classList.contains('is-visible') && isElementInViewport(target)) {
                target.classList.add('is-visible');
                observer.unobserve(target);
            }
        });
    };

    window.requestAnimationFrame(revealVisibleTargets);
    window.setTimeout(revealVisibleTargets, 180);
    window.setTimeout(revealVisibleTargets, 640);
    window.addEventListener('hashchange', () => window.setTimeout(revealVisibleTargets, 80), { passive: true });
};

const initLandingStaggers = (page: HTMLElement): void => {
    const containers = Array.from(page.querySelectorAll<HTMLElement>('[data-landing-stagger]'));

    if (!containers.length) {
        return;
    }

    containers.forEach(container => {
        const children = Array.from(container.children).filter(
            (child): child is HTMLElement => child instanceof HTMLElement
        );

        if (!children.length) {
            return;
        }

        if (prefersReducedMotion() || hasInitialHash()) {
            gsap.set(children, { autoAlpha: 1, y: 0, rotateX: 0, clearProps: 'filter,transform,visibility' });
            return;
        }

        let played = false;
        const prepare = (): void => {
            gsap.set(children, { autoAlpha: 0, y: 38, rotateX: 5, filter: 'blur(10px)' });
        };

        const play = (): void => {
            if (played) {
                return;
            }

            played = true;
            gsap.to(children, {
                autoAlpha: 1,
                y: 0,
                rotateX: 0,
                filter: 'blur(0px)',
                duration: 0.76,
                stagger: 0.075,
                ease: 'power3.out',
                clearProps: 'filter,transform,visibility'
            });
        };

        prepare();

        ScrollTrigger.create({
            trigger: container,
            start: 'top 82%',
            once: true,
            onEnter: play
        });

        const playIfVisible = (): void => {
            if (isElementInViewport(container, 0.92)) {
                play();
            }
        };

        window.requestAnimationFrame(playIfVisible);
        window.setTimeout(playIfVisible, 180);
        window.setTimeout(playIfVisible, 640);
        window.addEventListener('hashchange', () => window.setTimeout(playIfVisible, 80), { passive: true });
    });
};

const initLandingScenes = (page: HTMLElement): void => {
    const sections = Array.from(page.querySelectorAll<HTMLElement>('[data-landing-scene]'));
    const progress = page.querySelector<HTMLElement>('[data-landing-page-progress]');
    const footer = document.querySelector<HTMLElement>('.main-footer');

    if (!sections.length) {
        return;
    }

    setLandingScene(page, sections[0]);

    sections.forEach(section => {
        ScrollTrigger.create({
            trigger: section,
            start: 'top 34%',
            end: 'bottom 34%',
            onEnter: () => setLandingScene(page, section),
            onEnterBack: () => setLandingScene(page, section)
        });
    });

    if (progress) {
        ScrollTrigger.create({
            trigger: page,
            start: 'top top',
            end: 'bottom bottom',
            scrub: true,
            onUpdate: self => {
                progress.style.transform = `scaleY(${Math.max(0.001, self.progress).toFixed(4)})`;
            }
        });
    }

    if (footer) {
        ScrollTrigger.create({
            trigger: footer,
            start: 'top 82%',
            end: 'bottom bottom',
            onEnter: () => page.classList.add('is-footer-visible'),
            onEnterBack: () => page.classList.add('is-footer-visible'),
            onLeaveBack: () => page.classList.remove('is-footer-visible')
        });
    }

    if (prefersReducedMotion()) {
        return;
    }

    sections
        .filter(section => {
            const scene = section.dataset.landingScene;
            return scene !== 'hero' && scene !== 'story' && scene !== 'proof' && scene !== 'mentoring';
        })
        .forEach(section => {
            gsap.fromTo(
                section,
                { y: 28, autoAlpha: 0.96 },
                {
                    y: 0,
                    autoAlpha: 1,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 94%',
                        end: 'top 64%',
                        scrub: true
                    }
                }
            );
        });
};

const initLandingHero = (page: HTMLElement): void => {
    const hero = page.querySelector<HTMLElement>('[data-landing-hero]');
    if (!hero) {
        return;
    }

    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-landing-hero-reveal]'));
    const visual = hero.querySelector<HTMLElement>('[data-landing-hero-visual]');
    const portrait = hero.querySelector<HTMLElement>('[data-landing-portrait]');
    const depthLayers = Array.from(hero.querySelectorAll<HTMLElement>(
        '[data-landing-depth], .landing-hero__target, .landing-hero__shape, .landing-hero__sun'
    ));
    const grid = hero.querySelector<HTMLElement>('.landing-hero__grid');
    const scanline = hero.querySelector<HTMLElement>('.landing-hero__scanline');
    const kanji = hero.querySelector<HTMLElement>('.landing-hero__kanji');
    const prelude = page.querySelector<HTMLElement>('[data-landing-prelude]');
    const preludeItems = Array.from(prelude?.querySelectorAll<HTMLElement>('[data-landing-prelude-item]') ?? []);
    const preludeLine = prelude?.querySelector<HTMLElement>('[data-landing-prelude-line]');
    const preludeAperture = prelude?.querySelector<HTMLElement>('.landing-prelude__aperture');

    if (prefersReducedMotion()) {
        page.classList.add('is-reduced-motion');
        hero.classList.add('is-intro-complete');
        revealItems.forEach(item => item.classList.add('is-visible'));
        visual?.classList.add('is-visible');
        prelude?.remove();
        return;
    }

    hero.classList.add('is-intro-running');
    gsap.set(revealItems, { autoAlpha: 0, y: 30 });
    gsap.set(visual, { autoAlpha: 0, y: 34, scale: 0.96 });
    gsap.set(portrait, { autoAlpha: 0, y: 44, scale: 1.08, rotate: 1.6 });
    gsap.set(depthLayers, { autoAlpha: 0, y: 28, scale: 0.9, rotate: () => gsap.utils.random(-3, 3) });
    if (grid) {
        gsap.set(grid, { autoAlpha: 0, scale: 1.04 });
    }

    if (scanline) {
        gsap.set(scanline, { autoAlpha: 0, yPercent: -120 });
    }

    if (kanji) {
        gsap.set(kanji, { autoAlpha: 0, y: 56, scale: 0.96 });
    }

    if (prelude) {
        gsap.set(prelude, { autoAlpha: 1, clipPath: 'inset(0% 0% 0% 0%)' });
        if (preludeAperture) {
            gsap.set(preludeAperture, { autoAlpha: 0, scale: 0.94 });
        }
        gsap.set(preludeItems, { autoAlpha: 0, y: 24, filter: 'blur(8px)' });
        gsap.set(preludeLine, { scaleX: 0, autoAlpha: 1 });
    }

    let settled = false;
    const settle = (): void => {
        if (settled) {
            return;
        }

        settled = true;
        hero.classList.remove('is-intro-running');
        hero.classList.add('is-intro-complete');
        prelude?.remove();
        revealItems.forEach(item => item.classList.add('is-visible'));
        visual?.classList.add('is-visible');
        gsap.set(revealItems, { autoAlpha: 1, y: 0, clearProps: 'visibility' });
        gsap.set(visual, { autoAlpha: 1, y: 0, scale: 1, clearProps: 'visibility' });
        gsap.set(portrait, { autoAlpha: 1, y: 0, scale: 1, rotate: 0, clearProps: 'visibility' });
        gsap.set(depthLayers, { autoAlpha: 1, y: 0, scale: 1, rotate: 0, clearProps: 'visibility' });
        gsap.set([grid, scanline, kanji].filter((item): item is HTMLElement => item instanceof HTMLElement), {
            autoAlpha: 1,
            clearProps: 'visibility'
        });
    };

    const timeline = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: settle
    });

    if (prelude) {
        if (preludeAperture) {
            timeline.to(preludeAperture, {
                autoAlpha: 1,
                scale: 1,
                duration: 0.62,
                ease: 'power2.out'
            }, 0);
        }

        timeline
            .to(preludeItems, {
                autoAlpha: 1,
                y: 0,
                filter: 'blur(0px)',
                duration: 0.55,
                stagger: 0.085
            }, 0.08)
            .to(preludeLine, { scaleX: 1, duration: 0.72, ease: 'power2.inOut' }, 0.28);

        if (preludeAperture) {
            timeline.to(preludeAperture, {
                scale: 1.03,
                autoAlpha: 0,
                duration: 0.72,
                ease: 'power3.inOut'
            }, 0.86);
        }

        timeline
            .to(prelude, {
                clipPath: 'inset(0% 0% 100% 0%)',
                duration: 0.82,
                ease: 'power3.inOut'
            }, 0.92);
    }

    if (grid) {
        timeline.to(grid, { autoAlpha: 0.92, scale: 1, duration: 0.7 }, prelude ? 1.06 : 0);
    }

    if (scanline) {
        timeline.to(scanline, { autoAlpha: 0.72, yPercent: 112, duration: 1.16, ease: 'power2.inOut' }, prelude ? 1.12 : 0.05);
    }

    if (kanji) {
        timeline.to(kanji, { autoAlpha: 1, y: 0, scale: 1, duration: 0.9 }, prelude ? 1.18 : 0.12);
    }

    timeline
        .to(visual, { autoAlpha: 1, y: 0, scale: 1, duration: 0.86 }, prelude ? 1.28 : 0.22)
        .to(depthLayers, { autoAlpha: 1, y: 0, scale: 1, rotate: 0, duration: 0.92, stagger: 0.065 }, prelude ? 1.34 : 0.28)
        .to(portrait, { autoAlpha: 1, y: 0, scale: 1, rotate: 0, duration: 1.04 }, prelude ? 1.44 : 0.38)
        .to(revealItems, { autoAlpha: 1, y: 0, duration: 0.72, stagger: 0.085 }, prelude ? 1.62 : 0.58);

    window.setTimeout(settle, prelude ? 3400 : 2100);

    gsap.to(depthLayers, {
        y: index => (index % 2 === 0 ? -42 : 36),
        rotate: index => (index % 2 === 0 ? -2 : 2),
        ease: 'none',
        scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: 'bottom top',
            scrub: true
        }
    });

    if (portrait) {
        gsap.to(portrait, {
            scale: 1.04,
            y: 32,
            ease: 'none',
            scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: true
            }
        });
    }
};

const initLandingStory = (page: HTMLElement): void => {
    const story = page.querySelector<HTMLElement>('[data-landing-story]');
    const steps = Array.from(story?.querySelectorAll<HTMLElement>('[data-landing-story-step]') ?? []);
    const progress = story?.querySelector<HTMLElement>('[data-landing-story-progress]');

    if (!story || !steps.length) {
        return;
    }

    let activeIndex = 0;
    const setActive = (index: number): void => {
        const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
        activeIndex = nextIndex;
        const step = steps[nextIndex];
        if (step) {
            activateStoryStep(page, story, steps, step);
        }
    };

    setActive(0);

    if (prefersReducedMotion()) {
        steps.forEach(step => step.classList.add('is-active'));
        progress?.style.setProperty('transform', 'scaleY(1)');
        return;
    }

    ScrollTrigger.create({
        trigger: story,
        start: 'top 72%',
        end: 'bottom 32%',
        onEnter: () => page.classList.add('is-story-visible'),
        onEnterBack: () => page.classList.add('is-story-visible'),
        onLeave: () => page.classList.remove('is-story-visible'),
        onLeaveBack: () => page.classList.remove('is-story-visible')
    });

    steps.forEach((step, index) => {
        ScrollTrigger.create({
            trigger: step,
            start: 'top 58%',
            end: 'bottom 42%',
            onEnter: () => setActive(index),
            onEnterBack: () => setActive(index)
        });
    });

    if (progress) {
        const getSnapPoints = (): number[] => {
            const scrollDistance = Math.max(1, story.offsetHeight - window.innerHeight);
            const storyTop = story.getBoundingClientRect().top + window.scrollY;

            return steps.map(step => {
                const stepTop = step.getBoundingClientRect().top + window.scrollY;
                const targetScroll = stepTop + step.offsetHeight / 2 - window.innerHeight / 2;
                const progressValue = (targetScroll - storyTop) / scrollDistance;

                return Math.max(0, Math.min(1, progressValue));
            });
        };

        const isSnapLayout = window.matchMedia('(min-width: 901px)').matches;

        ScrollTrigger.create({
            trigger: story,
            start: 'top top',
            end: 'bottom bottom',
            scrub: true,
            ...(isSnapLayout
                ? {
                    snap: {
                        snapTo: (value: number) => gsap.utils.snap(getSnapPoints(), value),
                        duration: { min: 0.12, max: 0.28 },
                        delay: 0.04,
                        ease: 'power1.out'
                    }
                }
                : {}),
            onUpdate: self => {
                progress.style.transform = `scaleY(${Math.max(0.035, self.progress).toFixed(4)})`;
                const index = Math.min(steps.length - 1, Math.round(self.progress * (steps.length - 1)));
                if (index !== activeIndex) {
                    setActive(index);
                }
            }
        });
    }
};

const initLandingProof = (page: HTMLElement): void => {
    const proof = page.querySelector<HTMLElement>('[data-landing-proof]');
    const sticky = proof?.querySelector<HTMLElement>('.landing-proof__sticky');
    const lanes = Array.from(proof?.querySelectorAll<HTMLElement>('[data-landing-proof-lane]') ?? []);
    const spineDots = Array.from(proof?.querySelectorAll<HTMLElement>('.landing-proof__spine i') ?? []);

    if (!proof || !sticky || !lanes.length) {
        return;
    }

    const setActive = (index: number): void => {
        const nextIndex = Math.max(0, Math.min(index, lanes.length - 1));
        lanes.forEach((lane, laneIndex) => lane.classList.toggle('is-active', laneIndex === nextIndex));
        spineDots.forEach((dot, dotIndex) => {
            dot.classList.toggle('is-active', dotIndex === nextIndex);
            dot.classList.toggle('is-past', dotIndex < nextIndex);
        });
    };

    setActive(0);

    const isCompactLayout = window.matchMedia('(max-width: 900px)').matches;

    if (prefersReducedMotion() || isCompactLayout) {
        lanes.forEach(lane => lane.classList.add('is-active'));
        spineDots.forEach((dot, dotIndex) => {
            dot.classList.toggle('is-active', dotIndex === spineDots.length - 1);
            dot.classList.toggle('is-past', dotIndex < spineDots.length - 1);
        });
        proof.style.setProperty('--proof-progress', '1');
        return;
    }

    let activeIndex = 0;

    ScrollTrigger.create({
        trigger: proof,
        start: 'top top',
        end: 'bottom bottom',
        pin: sticky,
        pinSpacing: false,
        anticipatePin: 1,
        scrub: true,
        onUpdate: self => {
            proof.style.setProperty('--proof-progress', Math.max(0.001, self.progress).toFixed(4));
            const nextIndex = Math.round(self.progress * (lanes.length - 1));
            if (nextIndex !== activeIndex) {
                activeIndex = nextIndex;
                setActive(nextIndex);
            }
        }
    });
};

const initLandingMentoring = (page: HTMLElement): void => {
    const section = page.querySelector<HTMLElement>('#mentoring');
    const inner = section?.querySelector<HTMLElement>('.landing-mentoring__inner');
    const tabs = Array.from(section?.querySelectorAll<HTMLButtonElement>('[data-landing-mentoring-step]') ?? []);
    const cards = Array.from(section?.querySelectorAll<HTMLElement>('[data-landing-mentoring-card]') ?? []);

    if (!section || !inner || !tabs.length || !cards.length) {
        return;
    }

    let activeIndex = 0;
    const setActive = (index: number): void => {
        const nextIndex = Math.max(0, Math.min(index, cards.length - 1));
        activeIndex = nextIndex;

        tabs.forEach((tab, tabIndex) => {
            const isActive = tabIndex === nextIndex;
            tab.classList.toggle('is-active', isActive);
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        cards.forEach((card, cardIndex) => {
            const isActive = cardIndex === nextIndex;
            card.classList.toggle('is-active', isActive);
            card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
    };

    tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => setActive(index));
    });

    setActive(0);

    const isCompactLayout = window.matchMedia('(max-width: 1180px)').matches;
    if (prefersReducedMotion() || isCompactLayout) {
        return;
    }

    ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: 'bottom bottom',
        pin: inner,
        pinSpacing: false,
        anticipatePin: 1,
        scrub: true,
        onUpdate: self => {
            const nextIndex = Math.min(cards.length - 1, Math.round(self.progress * (cards.length - 1)));
            if (nextIndex !== activeIndex) {
                setActive(nextIndex);
            }
        }
    });
};

const restoreInitialHashScroll = (): void => {
    if (!hasInitialHash()) {
        return;
    }

    const targetId = window.location.hash.slice(1);
    const target = document.getElementById(decodeURIComponent(targetId));

    if (!target) {
        return;
    }

    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    target.scrollIntoView({ block: 'start' });
    document.documentElement.style.scrollBehavior = previousScrollBehavior;
};

document.addEventListener('DOMContentLoaded', () => {
    const page = document.querySelector<HTMLElement>('[data-landing-page]');
    if (!page) {
        return;
    }

    if (prefersReducedMotion()) {
        page.classList.add('is-reduced-motion');
    }

    initLandingReveals(page);
    initLandingStaggers(page);
    initLandingScenes(page);
    initLandingHero(page);
    initLandingStory(page);
    initLandingProof(page);
    initLandingMentoring(page);
    restoreInitialHashScroll();
    window.addEventListener('load', () => {
        restoreInitialHashScroll();
        ScrollTrigger.refresh();
    }, { once: true });
    window.addEventListener('resize', () => ScrollTrigger.refresh(), { passive: true });
});
